/**
 * BboxCropThumbnail (#170) — an instant item thumbnail cropped from the
 * source scan photo + the item's bbox, for when the polished cutout
 * (`primary_photo_url`) doesn't exist yet.
 *
 * Why: the backend generates `primary_photo_url` (the AuraSR cutout)
 * asynchronously AFTER save. At scan-review time — and on a freshly
 * saved scene before the crop lands — items have no cutout, so a plain
 * thumbnail falls to a gray placeholder (every item looking identical /
 * empty). But we already have everything needed to show a real, distinct
 * thumbnail client-side: the full scan photo + each item's bbox. This
 * component "cover"-crops the bbox region into a square cell — no
 * backend dependency, instant.
 *
 * `bbox` is in the source image's natural pixel coordinates (same space
 * `transform.toDomBbox` consumes). We measure the photo's natural size
 * once via `Image.getSize` (cached by the browser across cells sharing
 * one source URL), then scale + translate the image so the bbox fills
 * the cell. Falls back to the icon placeholder if the size can't be read.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, View, type ImageStyle, type StyleProp } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

export interface CropBbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  sourceUrl: string;
  bbox: CropBbox | null | undefined;
  size: number;
  shape?: 'square' | 'circle';
  style?: StyleProp<ImageStyle>;
  testID?: string;
}

// Module-level memo of natural sizes so N cells sharing one photo only
// measure once. (Date.now/Math.random unused — safe for resume.)
const sizeCache = new Map<string, { w: number; h: number }>();

export const BboxCropThumbnail: React.FC<Props> = ({
  sourceUrl,
  bbox,
  size,
  shape = 'square',
  style,
  testID,
}) => {
  const [nat, setNat] = useState<{ w: number; h: number } | null>(
    () => sizeCache.get(sourceUrl) ?? null,
  );
  const [errored, setErrored] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const cached = sizeCache.get(sourceUrl);
    if (cached) {
      setNat(cached);
    } else if (sourceUrl) {
      Image.getSize(
        sourceUrl,
        (w, h) => {
          if (w > 0 && h > 0) sizeCache.set(sourceUrl, { w, h });
          if (aliveRef.current) setNat(w > 0 && h > 0 ? { w, h } : null);
        },
        () => {
          if (aliveRef.current) setErrored(true);
        },
      );
    }
    return () => {
      aliveRef.current = false;
    };
  }, [sourceUrl]);

  const radius = shape === 'circle' ? size / 2 : 8;

  const valid =
    bbox &&
    typeof bbox.w === 'number' &&
    typeof bbox.h === 'number' &&
    bbox.w > 0 &&
    bbox.h > 0;

  if (!sourceUrl || !valid || errored || !nat) {
    return (
      <View
        style={[{ width: size, height: size, borderRadius: radius }, styles.placeholder, style as any]}
        testID={testID}
      >
        <MaterialIcons name="image" size={Math.max(14, Math.round(size * 0.42))} color={fpColors.outline} />
      </View>
    );
  }

  // "cover" the square cell with the bbox region, centered.
  const scale = size / Math.min(bbox!.w, bbox!.h);
  const imgW = nat.w * scale;
  const imgH = nat.h * scale;
  const cx = (bbox!.x + bbox!.w / 2) * scale;
  const cy = (bbox!.y + bbox!.h / 2) * scale;
  const left = size / 2 - cx;
  const top = size / 2 - cy;

  return (
    <View
      style={[{ width: size, height: size, borderRadius: radius }, styles.clip, style as any]}
      testID={testID}
    >
      <Image
        source={{ uri: sourceUrl }}
        style={{ position: 'absolute', width: imgW, height: imgH, left, top }}
        onError={() => setErrored(true)}
        accessibilityIgnoresInvertColors
      />
    </View>
  );
};

const styles = StyleSheet.create({
  clip: { overflow: 'hidden', backgroundColor: '#000' },
  placeholder: {
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default BboxCropThumbnail;
